import axios, { type InternalAxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'
import { useAuthStore } from '@/store/auth'
import { StaleTenantResponseError, isStaleTenantResponse } from '@/utils/tenantStamp'

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
})

// 是否正在刷新token
let isRefreshing = false
// 等待刷新的请求队列
let pendingRequests: Array<(token: string) => void> = []

// 请求拦截器 — 自动附加 JWT 和 X-Tenant-Id，并盖请求时刻的租户戳（1102-FE4）
api.interceptors.request.use((config: InternalAxiosRequestConfig & { __stampedTenantId?: string | null }) => {
  const authStore = useAuthStore()
  const token = localStorage.getItem('access_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  // 自动添加入驻企业ID（使用effectiveTenantId）
  // 注意：effectiveTenantId是computed property，需要等待auth store初始化完成
  const tenantId = authStore.effectiveTenantId || localStorage.getItem('selected_tenant_id')
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId
  }
  // 1102-FE4：把「发请求那一刻」的租户ID盖下来。响应回来时若当前租户已切换，
  // 该响应视为过期，由响应拦截器静默丢弃，避免旧企业数据写进新页面。
  config.__stampedTenantId = tenantId ?? null
  return config
})

/**
 * 刷新 access_token
 * 注意：后端统一返回 {code, message, data}，HTTP 状态恒为 200，
 * 认证失败用 code=401 表示（如 "Not authenticated"）。
 */
async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) throw new Error('No refresh token')

  const res: any = await axios.post('/api/v1/auth/refresh', { refresh_token: refreshToken })
  // res.data = {code, message, data:{access_token, refresh_token, user}}
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '刷新令牌失败')
  }
  const refreshData = res.data.data
  const newToken = refreshData.access_token
  const newUser = refreshData.user

  localStorage.setItem('access_token', newToken)
  localStorage.setItem('refresh_token', refreshData.refresh_token)

  const authStore = useAuthStore()
  authStore.user = newUser
  return newToken
}

// 响应拦截器 — 统一处理错误和响应格式
api.interceptors.response.use(
  async (response) => {
    // 1102-FE4：请求时刻的租户戳 ≠ 当前租户 ⇒ 是切租户前的在途响应，丢弃。
    // 业务 store 拿不到这次响应，页面也不会因此重渲；后端 401/200 分支都不再处理它。
    // blob/text 导出请求同样盖戳，过期时也丢（用户不会在切租户后仍看到上一企业的导出文件）。
    const stamped = (response.config as { __stampedTenantId?: string | null }).__stampedTenantId ?? null
    const authStore0 = useAuthStore()
    const currentTenantId = authStore0.effectiveTenantId ?? (localStorage.getItem('selected_tenant_id') || null)
    if (isStaleTenantResponse(stamped, currentTenantId)) {
      return Promise.reject(new StaleTenantResponseError(stamped, currentTenantId))
    }

    // blob响应直接返回原始response，不处理业务状态码
    // text响应（导出 HTML 等）也直接返回，避免被错误地当 JSON 解析
    if (response.config.responseType === 'blob' || response.config.responseType === 'text') {
      return response
    }

    const res = response.data

    // 1103-R8：HTTP 2xx 但响应体为空（真 204 / 后端误留）⇒ 规范化为成功。
    // 与其他成功路径同形（{code:200, message, data}），避免调用方 res.data 取值炸掉。
    // 只作用于 HTTP 2xx（此处为 axios 的成功分支），非 2xx 仍走下方业务码 / error 分支。
    if (
      response.status >= 200 && response.status < 300 &&
      (res === null || res === undefined || (typeof res === 'object' && Object.keys(res).length === 0))
    ) {
      return { code: 200, message: (res && res.message) || 'success', data: (res && res.data) ?? null }
    }

    // 成功直接返回
    if (res.code === 200) return res

    const authStore = useAuthStore()

    // 主动登出期间：静默处理，不弹 "Not authenticated" 等错误
    if (authStore.isLoggingOut) {
      return Promise.resolve({ code: 200, message: '', data: null })
    }

    // 401 → 尝试刷新 token 后重放原请求
    if (res.code === 401) {
      const originalRequest = response.config

      // 已经是重试过的请求，说明刷新也失败了
      if ((originalRequest as any)._retry) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        authStore.logout()
        router.push('/login')
        ElMessage.error('登录已过期，请重新登录')
        return Promise.reject(new Error('登录已过期，请重新登录'))
      }

      // 如果已经在刷新token，将请求加入队列
      if (isRefreshing) {
        return new Promise((resolve) => {
          pendingRequests.push((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(api(originalRequest))
          })
        })
      }

      (originalRequest as any)._retry = true
      isRefreshing = true

      try {
        const newToken = await refreshAccessToken()

        // 处理队列中的请求
        pendingRequests.forEach(callback => callback(newToken))
        pendingRequests = []

        // 重试原始请求
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch (refreshError) {
        // 刷新失败，清空队列并跳转登录
        pendingRequests.forEach(callback => callback(''))
        pendingRequests = []
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        authStore.logout()
        ElMessage.error('登录已过期，请重新登录')
        router.push('/login')
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // 其他业务错误：把业务码挂到 error 上，页面可据此分流
    // （404 接口未上线 vs 404 航次不存在 vs 403 权限，三者文案与处置不同）
    ElMessage.error(res.message || '请求失败')
    const bizError = new Error(res.message || '请求失败') as Error & { code?: number }
    bizError.code = res.code
    return Promise.reject(bizError)
  },
  // HTTP 层错误。batch2 既有“真 4xx + body.code”，也有“HTTP 200 + body.code=4xx”；
  // 这里把真 4xx 也归一为可被页面读取的业务错误，避免页面只看到 Axios 通用文案。
  (error) => {
    const authStore = useAuthStore()
    if (authStore.isLoggingOut) {
      return Promise.resolve({ code: 200, message: '', data: null })
    }
    // 1102-FE4：切租户前发出的在途响应被丢弃 ⇒ 静默拒绝，不弹 ElMessage、不进页面逻辑。
    // 上层调用方拿到 rejected promise，但都是「fire-and-forget」的列表加载，组件 catch 忽略即可。
    if (error instanceof StaleTenantResponseError) {
      return Promise.reject(error)
    }
    const body = error.response?.data
    const message = body?.message || error.message || '网络错误'
    ElMessage.error(message)
    if (body && typeof body === 'object') {
      const normalized = new Error(message) as Error & {
        code?: number
        httpStatus?: number
        data?: unknown
        response?: unknown
      }
      normalized.code = typeof body.code === 'number' ? body.code : error.response?.status
      normalized.httpStatus = error.response?.status
      normalized.data = body.data
      normalized.response = error.response
      return Promise.reject(normalized)
    }
    return Promise.reject(error)
  }
)

export default api
